<template>
  <div class="AIDocumentAnalysis mt-1">
    <v-select
      class="mb-3"
      outlined
      dense
      :value="selectedNode.settings.model || defaultModel.value"
      @input="(val) => onUpdate(val, 'settings.model')"
      :items="models"
      item-text="label"
      item-value="value"
      label="AI Model"
    ></v-select>

    <v-row dense align="center">
      <v-col cols="auto" class="mr-auto">
        <v-switch
          label="Visual Mode"
          v-model="selectedNode.settings.visualMode"
          @change="(val) => onUpdate(val, `settings.visualMode`)"
        >
          Visual Mode
        </v-switch>
      </v-col>
    </v-row>

    <v-select
      class="mt-2 mb-3"
      outlined
      dense
      :value="selectedNode.settings.file"
      @input="(val) => onUpdate(val, 'settings.file')"
      item-text="name"
      item-value="name"
      :items="availableInputFields"
      label="PDF Base64"
      clearable
    >
    </v-select>

    <v-row dense align="center">
      <v-col cols="auto" class="mr-auto">
        <v-switch
          label="Classify"
          v-model="selectedNode.settings.classify"
          @change="(val) => onUpdate(val, `settings.classify`)"
        >
          Classify
        </v-switch>
      </v-col>
    </v-row>

    <div v-if="selectedNode.settings.classify">
      <v-text-field
        outlined
        dense
        type="number"
        label="Max Pages"
        :value="selectedNode.settings.maxPages"
        @input="(val) => onUpdate(val, 'settings.maxPages')"
        class="mb-2"
        :disabled="selectedNode.settings.split || selectedNode.settings.extract"
      />

      <!-- Quick Add from API -->
      <div class="mb-4" v-if="availableClassifications.length > 0">
        <div class="text-body-2 font-weight-medium mb-2">📁 Quick Add:</div>
        <div class="d-flex flex-wrap gap-2">
          <v-btn
            v-for="classification in availableClassifications"
            :key="classification.id"
            small
            outlined
            color="primary"
            class="mr-2 mb-2"
            @click="addClassificationFromAPI(classification)"
            :loading="documentClassificationsLoading"
          >
            {{ classification.documentType }}
          </v-btn>
        </div>
      </div>

      <!-- Classification Rules -->
      <div
        v-for="(rule, index) in currentClassifications"
        :key="index"
        class="mb-4"
      >
        <v-card outlined class="pa-3">
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="d-flex align-center">
              <div class="text-body-2 font-weight-medium mr-2">
                Rule {{ index + 1 }}
              </div>
              <v-chip
                x-small
                :color="rule.isCustom ? 'blue' : 'green'"
                dark
                v-if="rule.isCustom"
              >
                Custom
              </v-chip>
            </div>
            <v-btn
              icon
              small
              color="error"
              @click="removeClassification(index)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>

          <v-text-field
            outlined
            dense
            label="Document Type"
            placeholder="e.g., invoice, receipt, contract"
            :value="rule.documentType"
            @input="
              (val) => {
                updateClassification(index, 'documentType', val);
                updateClassification(index, 'isCustom', true);
              }
            "
            class="mb-2"
          />

          <v-textarea
            outlined
            dense
            label="Description (min 10 characters)"
            placeholder="Describe what characteristics identify this document type (minimum 10 characters)..."
            :value="rule.description"
            @input="
              (val) => {
                updateClassification(index, 'description', val);
                updateClassification(index, 'isCustom', true);
              }
            "
            :rules="[rules.minLength]"
            rows="3"
          />
        </v-card>
      </div>

      <!-- Add Custom Rule Button -->
      <v-btn
        outlined
        color="primary"
        class="mb-4"
        @click.stop="() => addCustomRule()"
      >
        <v-icon left small>mdi-plus</v-icon>
        Add Custom Rule
      </v-btn>
    </div>

    <v-row dense align="center">
      <v-col cols="auto" class="mr-auto">
        <v-switch
          label="Split"
          v-model="selectedNode.settings.split"
          @change="(val) => onUpdate(val, `settings.split`)"
        >
          Split
        </v-switch>
      </v-col>
    </v-row>

    <div v-if="selectedNode.settings?.split">
      <v-switch
        class="mb-2"
        label="Override built-in split prompt"
        :input-value="!!selectedNode.settings.overrideSplitPrompt"
        @change="(val) => onUpdate(!!val, 'settings.overrideSplitPrompt')"
      />
      <p class="text-caption grey--text mb-2">
        {{
          selectedNode.settings.overrideSplitPrompt
            ? 'Your instructions replace the default split prompt entirely. The model response must still be raw JSON with structure "single" or "multiple" (and sections with page ranges when multiple), or split parsing will fail.'
            : 'Your instructions are appended to the default split prompt.'
        }}
      </p>
      <v-textarea
        outlined
        dense
        :label="
          selectedNode.settings.overrideSplitPrompt
            ? 'Split Prompt'
            : 'Additional Split rules'
        "
        :placeholder="
          selectedNode.settings.overrideSplitPrompt
            ? 'Write the full split prompt for the AI model...'
            : 'Describe additional split rules for AI model...'
        "
        :value="selectedNode.settings.additionalSplitRules"
        @input="(val) => onUpdate(val, 'settings.additionalSplitRules')"
        :rows="selectedNode.settings.overrideSplitPrompt ? 8 : 3"
      />
    </div>

    <v-row dense align="center">
      <v-col cols="auto" class="mr-auto">
        <v-switch
          label="Extract"
          v-model="selectedNode.settings.extract"
          @change="(val) => onUpdate(val, `settings.extract`)"
        >
          Extract
        </v-switch>
      </v-col>
    </v-row>

    <div v-if="selectedNode.settings.extract">
      <div class="mb-4" v-if="availableExtractTemplates?.length > 0">
        <div class="text-body-2 font-weight-medium mb-2">🔧 Quick Add:</div>
        <div class="d-flex flex-wrap gap-2">
          <v-btn
            v-for="template in availableExtractTemplates"
            :key="template.id"
            small
            outlined
            color="primary"
            class="mr-2 mb-2"
            @click="addExtractTemplateFromAPI(template)"
            :loading="documentExtractTemplatesLoading"
          >
            <v-icon left small>mdi-code-json</v-icon>
            {{ template.name }}
          </v-btn>
        </div>
      </div>

      <div v-if="currentExtractTemplate" class="mb-4">
        <v-card outlined class="pa-4">
          <div class="d-flex mb-3 justify-end gap-2">
            <v-btn outlined small color="primary" @click="editTemplate">
              <v-icon left small>mdi-pencil</v-icon>
              Edit
            </v-btn>
            <v-btn outlined small color="error" @click="removeExtractTemplate">
              <v-icon left small>mdi-delete</v-icon>
              Remove
            </v-btn>
          </div>

          <div class="mb-3">
            <div class="d-flex align-center">
              <div class="text-h6 mr-2">{{ currentExtractTemplate.name }}</div>
              <v-chip
                x-small
                :color="currentExtractTemplate.isCustom ? 'blue' : 'green'"
                dark
                v-if="currentExtractTemplate.isCustom"
                class="mr-2"
              >
                Custom
              </v-chip>
              <v-btn
                v-if="shouldShowExpandButton"
                text
                x-small
                color="primary"
                class="pa-0"
                style="min-width: auto"
                @click="toggleDescriptionExpanded"
              >
                {{ descriptionExpanded ? 'Show less' : 'Show more' }}
              </v-btn>
            </div>
            <div class="text-body-2 text--secondary">
              {{ truncatedDescription }}
            </div>
          </div>

          <div class="mb-3">
            <div class="text-body-2 font-weight-medium mb-2">
              Fields Summary:
            </div>
            <div v-if="templateFieldsCount > 0" class="d-flex flex-wrap gap-1">
              <v-chip
                v-for="(fieldInfo, index) in templateFieldsSummary"
                :key="index"
                small
                outlined
                :color="fieldInfo.required ? 'primary' : 'default'"
              >
                <v-icon left x-small>{{ fieldInfo.icon }}</v-icon>
                {{ fieldInfo.name }}
                <span v-if="fieldInfo.required" class="ml-1">*</span>
              </v-chip>
            </div>
            <div v-else class="text-caption text--secondary">
              No fields defined yet
            </div>
          </div>

          <div class="d-flex text-caption text--secondary gap-4">
            <span
              ><strong>{{ templateFieldsCount }}</strong> total fields</span
            >
            <span
              ><strong>{{ requiredFieldsCount }}</strong> required</span
            >
            <span
              ><strong>{{ objectFieldsCount }}</strong> nested objects</span
            >
          </div>
        </v-card>
      </div>

      <div v-else class="mb-4 text-center">
        <v-btn outlined color="primary" @click="createTemplate">
          <v-icon left small>mdi-plus</v-icon>
          Create Custom Template
        </v-btn>
      </div>

      <extract-template-modal
        :visible="showTemplateModal"
        :template="templateBeingEdited"
        @close="closeTemplateModal"
        @save="saveTemplateFromModal"
      />
    </div>
  </div>
</template>

<script>
import { availableAIModels, defaultModel } from '@/util/availableAIModels';
import 'vue-json-viewer/style.css';
import { createNamespacedHelpers } from 'vuex';
import ExtractTemplateModal from './ExtractTemplateModal.vue';

const { mapGetters: workflowDetailsGetters } =
  createNamespacedHelpers('workflows/details');

const {
  mapGetters: documentAnalysisGetters,
  mapActions: documentAnalysisActions,
} = createNamespacedHelpers('documentAnalysis');

export default {
  name: 'ai-document-analysis-settings',
  components: {
    ExtractTemplateModal,
  },
  props: {
    availableInputFields: { required: true },
  },
  data() {
    return {
      showDialog: false,
      showPromt: '',
      error: '',
      output: '',
      models: availableAIModels,
      defaultModel,
      placeholder: `
      Example:
      function myFunction(data) {
        return 'Hello ' + data.patientName
      }
      myFunction(data)`,
      showTemplateModal: false,
      templateBeingEdited: null,
      descriptionExpanded: false,
    };
  },
  watch: {
    'selectedCustomer.customer_id'() {},
  },
  computed: {
    ...workflowDetailsGetters({
      selectedNode: 'SELECTED_NODE',
    }),
    ...documentAnalysisGetters({
      availableClassifications: 'DOCUMENT_CLASSIFICATIONS',
      documentClassificationsLoading: 'DOCUMENT_CLASSIFICATIONS_LOADING',
      availableExtractTemplates: 'DOCUMENT_EXTRACT_TEMPLATES',
      documentExtractTemplatesLoading: 'DOCUMENT_EXTRACT_TEMPLATES_LOADING',
    }),
    currentClassifications() {
      return this.selectedNode?.settings?.classifications || [];
    },
    currentExtractTemplate() {
      return this.selectedNode?.settings?.extractTemplate || null;
    },
    templateFieldsCount() {
      return this.countAllFields(
        this.currentExtractTemplate?.schema?.properties || {},
      );
    },
    requiredFieldsCount() {
      return this.currentExtractTemplate?.schema?.required?.length || 0;
    },
    objectFieldsCount() {
      return this.countObjectFields(
        this.currentExtractTemplate?.schema?.properties || {},
      );
    },
    templateFieldsSummary() {
      return this.getFieldsSummary(
        this.currentExtractTemplate?.schema?.properties || {},
        this.currentExtractTemplate?.schema?.required || [],
      );
    },
    rules() {
      return {
        minLength: (value) =>
          (value && value.length >= 10) || 'Minimum 10 characters required',
      };
    },
    truncatedDescription() {
      if (!this.currentExtractTemplate?.description) return '';
      const maxLength = 150; // Maximum characters to show when collapsed
      const description = this.currentExtractTemplate.description;

      if (this.descriptionExpanded || description.length <= maxLength) {
        return description;
      }

      return description.substring(0, maxLength) + '...';
    },
    shouldShowExpandButton() {
      if (!this.currentExtractTemplate?.description) return false;
      return this.currentExtractTemplate.description.length > 150;
    },
  },
  async created() {
    if (!this.selectedNode?.settings?.model) {
      this.onUpdate(defaultModel.value, 'settings.model');
    }

    await this.fetchDocumentClassifications();
    await this.fetchDocumentExtractTemplates();
  },
  methods: {
    ...documentAnalysisActions([
      'fetchDocumentClassifications',
      'fetchDocumentExtractTemplates',
    ]),
    onUpdate(value, path) {
      this.$emit('update', value, path);
    },
    addClassificationFromAPI(classification) {
      const newClassification = {
        documentType: classification.documentType,
        description: classification.description,
        id: classification.id,
        isCustom: false,
      };

      const currentClassifications = [...this.currentClassifications];

      const exists = currentClassifications.find(
        (c) => c.id === classification.id,
      );
      if (!exists) {
        currentClassifications.push(newClassification);
        this.onUpdate(currentClassifications, 'settings.classifications');
      }
    },
    addCustomRule() {
      const newClassification = {
        documentType: '',
        description: '',
        isCustom: true,
      };

      this.currentClassifications.push(newClassification);
      const currentClassifications = [...this.currentClassifications];
      this.onUpdate(currentClassifications, 'settings.classifications');
    },
    removeClassification(index) {
      const currentClassifications = [...this.currentClassifications];
      currentClassifications.splice(index, 1);
      this.onUpdate(currentClassifications, 'settings.classifications');
    },
    updateClassification(index, field, value) {
      const currentClassifications = [...this.currentClassifications];
      currentClassifications[index] = {
        ...currentClassifications[index],
        [field]: value,
      };
      this.onUpdate(currentClassifications, 'settings.classifications');
    },
    addExtractTemplateFromAPI(template) {
      const extractTemplate = {
        id: template.id,
        name: template.name,
        description: template.description,
        schema: JSON.parse(JSON.stringify(template.schema)),
        isCustom: false,
      };

      this.onUpdate(extractTemplate, 'settings.extractTemplate');
    },

    createTemplate() {
      this.templateBeingEdited = null;
      this.showTemplateModal = true;
    },
    editTemplate() {
      this.templateBeingEdited = JSON.parse(
        JSON.stringify(this.currentExtractTemplate),
      );
      this.showTemplateModal = true;
    },
    closeTemplateModal() {
      this.showTemplateModal = false;
      this.templateBeingEdited = null;
    },
    saveTemplateFromModal(templateData) {
      this.onUpdate(templateData, 'settings.extractTemplate');
      this.onUpdate(true, 'settings.extractTemplate.isCustom');
    },
    removeExtractTemplate() {
      this.onUpdate(null, 'settings.extractTemplate');
    },
    toggleDescriptionExpanded() {
      this.descriptionExpanded = !this.descriptionExpanded;
    },

    countAllFields(properties, depth = 0) {
      let count = 0;
      for (const [, field] of Object.entries(properties || {})) {
        count++;
        if (field.type === 'object' && field.properties) {
          count += this.countAllFields(field.properties, depth + 1);
        }
      }
      return count;
    },
    countObjectFields(properties) {
      let count = 0;
      for (const [, field] of Object.entries(properties || {})) {
        if (field.type === 'object') {
          count++;
          if (field.properties) {
            count += this.countObjectFields(field.properties);
          }
        }
      }
      return count;
    },
    getFieldsSummary(properties, required = [], parentPath = '') {
      const summary = [];
      const maxFields = 10; // Limit displayed fields

      for (const [fieldName, field] of Object.entries(properties || {})) {
        if (summary.length >= maxFields) break;

        const fullPath = parentPath ? `${parentPath}.${fieldName}` : fieldName;
        const isRequired = required.includes(fieldName);

        summary.push({
          name: fullPath,
          type: field.type,
          required: isRequired,
          icon: this.getFieldIcon(field.type),
        });

        if (
          field.type === 'object' &&
          field.properties &&
          parentPath.split('.').length < 2
        ) {
          const nestedSummary = this.getFieldsSummary(
            field.properties,
            field.required || [],
            fullPath,
          );
          summary.push(
            ...nestedSummary.slice(0, Math.max(0, maxFields - summary.length)),
          );
        }
      }

      return summary;
    },
    getFieldIcon(fieldType) {
      const iconMap = {
        string: 'mdi-format-text',
        number: 'mdi-numeric',
        integer: 'mdi-numeric',
        boolean: 'mdi-checkbox-outline',
        array: 'mdi-format-list-bulleted',
        object: 'mdi-code-braces',
      };
      return iconMap[fieldType] || 'mdi-help-circle';
    },
  },
};
</script>

<style scoped lang="scss">
@import './ai-document-analysis';

.gap-1 > * + * {
  margin-left: 4px;
}

.gap-2 > * + * {
  margin-left: 8px;
}

.gap-4 > * + * {
  margin-left: 16px;
}
</style>
